import { Entity } from '@app/core';
import { ReportScope } from '@games/interfaces';

export interface GroupPermission {
  id: number;
  name: string;
  has_access: boolean;
}

export interface ReportPermission {
  id: number;
  name: string;
  report_scope: string;
  group_permissions?: GroupPermission[];
}

export interface ReportPermissionResponse {
  reports: ReportPermission[];
}

export interface IReportEntity {
  id: number;
  name: string;
  reportScope: ReportScope | string;
  reportScopeLabel?: string;
  groupPermissions?: GroupPermission[];
}

export class ReportEntity extends Entity<IReportEntity> {
  constructor(props: IReportEntity) {
    super(props);
  }

  get id(): number {
    return this.props.id;
  }

  get name(): string {
    return this.props.name;
  }

  get scope(): ReportScope | string {
    return this.props.reportScope;
  }

  get scopeLabel(): string {
    return this.props.reportScopeLabel ?? '';
  }

  get groupPermissions(): GroupPermission[] {
    return this.props.groupPermissions ?? [];
  }

  get isEntity(): boolean {
    return this.matchesScope(ReportScope.ENTITY, 'E');
  }

  get isEconomy(): boolean {
    return this.matchesScope(ReportScope.ECONOMY, 'S');
  }

  get isMarket(): boolean {
    return this.matchesScope(ReportScope.MARKET, 'G');
  }

  get isDday(): boolean {
    return this.props.name?.toUpperCase() === 'DDAY';
  }

  hasAccess(groupId: number): boolean {
    return this.groupPermissions.some(gp => gp.id === groupId && gp.has_access);
  }

  /**
   * The API reports scope either as a single letter code (E/S/G) or as its
   * display name (Entity/Economy/Market), so accept both spellings.
   */
  private matchesScope(displayName: ReportScope, code: string): boolean {
    const scope = `${this.props.reportScope ?? ''}`.trim();

    return scope.toUpperCase() === code || scope.toLowerCase() === displayName.toLowerCase();
  }
}

// Backward compatibility alias
export { ReportEntity as ReportType };
